import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { QuizResultEnum } from '@/types/schema';

import QuizMetricsGridItem from '../QuizReviewMetricsGridItem';
import { renderScoreInfo } from './renderers';
import { QuizMetricsProps } from './types';
import { getReviewGridItems } from './utils';

function QuizReviewMetrics(props: QuizMetricsProps) {
  const { t } = useTranslation();
  const {
    overrides,
    correctAnswersCount,
    partiallyCorrectAnswersCount,
    totalQuestionsCount,
    scorePercentage,
    passingPercentage,
    timeSpent,
    score,
    maxScore,
    result,
    quizUser,
  } = props;

  const user = useUserContext();

  const isSelf = user.id === quizUser?.id;

  const gridItems = getReviewGridItems({
    t,
    correctCount: correctAnswersCount,
    partiallyCorrectCount: partiallyCorrectAnswersCount,
    incorrectCount: totalQuestionsCount - correctAnswersCount - partiallyCorrectAnswersCount,
    passingPercentage,
    score,
    maxScore,
    timeSpent,
  });

  return (
    <>
      {result === QuizResultEnum.Pass ? (
        <Stack className="mt-24 ps-8" direction="col" gapY={4}>
          <Typography className="xs:body-short-01" color="text-text-secondary" variant="label-02">
            {t('QuizPlayer:WellDone')}
          </Typography>
          <Typography className="xs:heading-l" variant="heading-m">
            {isSelf
              ? t('QuizPlayer:YouPassed')
              : t('QuizPlayer:UserPassed', { name: quizUser?.name || '' })}
          </Typography>
        </Stack>
      ) : null}
      {result === QuizResultEnum.Fail ? (
        <Stack className="mt-24 ps-8" direction="col" gapY={4}>
          <Typography className="xs:body-short-01" color="text-text-secondary" variant="label-02">
            {t('QuizPlayer:UhOh')}
          </Typography>
          <Typography className="xs:heading-l" variant="heading-m">
            {isSelf
              ? t('QuizPlayer:YouFailed')
              : t('QuizPlayer:UserFailed', { name: quizUser?.name || '' })}
          </Typography>
        </Stack>
      ) : null}
      <Stack
        alignItems="start"
        className="mt-24 rounded-12 border border-border py-16 me-20"
        direction="col">
        <Stack className="w-full px-20 pb-20 border-b border-border" justifyContent="between">
          {renderScoreInfo({
            t,
            scorePercentage,
            passingPercentage,
            result,
          })}
        </Stack>
        <Stack className="mt-16 px-20" direction="col" gapY={26}>
          {gridItems.map((item, index) => (
            <QuizMetricsGridItem key={index} {...item} />
          ))}
        </Stack>
      </Stack>
    </>
  );
}

export default QuizReviewMetrics;
